export interface ApiProblem {
  status: number;
  code: string;
  title: string;
  detail: string;
  requestId?: string;
  violations?: Array<{ field: string; message: string }>;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly title: string;
  readonly requestId?: string;
  readonly violations: Array<{ field: string; message: string }>;

  constructor(problem: ApiProblem) {
    super(problem.detail || problem.title);
    this.name = "ApiError";
    this.status = problem.status;
    this.code = problem.code;
    this.title = problem.title;
    this.requestId = problem.requestId;
    this.violations = problem.violations ?? [];
  }
}

export interface CurrentSession {
  subjectId: string;
  username: string;
  displayName: string;
  sessionId: string;
  authenticatedAt: string;
  authenticationMethod: string;
  assuranceLevel: number;
  stepUpAt: string | null;
  secondFactorRequired: boolean;
}

export interface Account {
  id: string;
  status: "ACTIVE" | "DELETION_PENDING";
  username: string;
  displayName: string;
  locale: string;
  timeZone: string;
  email: { address: string; verified: boolean; verifiedAt: string | null };
  createdAt: string;
  updatedAt: string;
  version: number;
}

export interface SessionView {
  id: string;
  createdAt: string;
  lastAccessedAt: string;
  expiresAt: string;
  current: boolean;
  authenticationMethod: string;
  userAgent: string | null;
}

export interface MfaStatus {
  totpEnabled: boolean;
  recoveryCodeGeneration: number;
  recoveryCodeTotal: number;
  recoveryCodeRemaining: number;
  recoveryCodesRotatedAt: string | null;
  passkeyCount: number;
  assurance: {
    level: number;
    method: string | null;
    verifiedAt: string | null;
    expiresAt: string | null;
    recent: boolean;
  };
}

export interface PasskeyView {
  id: string;
  label: string;
  transports: string[];
  backupEligible: boolean;
  backedUp: boolean;
  createdAt: string;
  lastUsedAt: string | null;
}

export interface ExternalIdentity {
  id: string;
  provider: string;
  linkedAt: string;
  lastUsedAt: string | null;
}

export interface ExternalProvider {
  id: string;
  displayName: string;
}

export interface ExternalProviderConfiguration extends ExternalProvider {
  enabled: boolean;
  clientId: string;
  clientSecretConfigured: boolean;
  teamId: string | null;
  keyId: string | null;
  privateKeyConfigured: boolean;
  source: "ENVIRONMENT" | "DATABASE";
  redirectUri: string;
  updatedAt: string | null;
  version: number;
}

export interface RegistrationPublicPolicy {
  registrationEnabled: boolean;
  invitationRequired: boolean;
  emailVerificationRequired: boolean;
}

export interface RegistrationLimit {
  permits: number;
  window: string;
}

export interface RegistrationPolicy extends RegistrationPublicPolicy {
  emailAdmissionMode: "ALL" | "ALLOWLIST";
  registrationIpLimit: RegistrationLimit;
  registrationIdentifierLimit: RegistrationLimit;
  resendIpLimit: RegistrationLimit;
  resendIdentifierLimit: RegistrationLimit;
  version: number;
}

export interface RegistrationAllowlistEntry {
  id: string;
  type: "ADDRESS" | "DOMAIN";
  value: string;
  createdAt: string;
}

export interface RegistrationInvitation {
  id: string;
  codeHint: string;
  status: "ACTIVE" | "REVOKED";
  maxUses: number;
  useCount: number;
  expiresAt: string | null;
  createdAt: string;
  version: number;
}

export interface RegistrationInvitationUse {
  invitationId: string;
  subjectId: string;
  username: string | null;
  usedAt: string;
}

export interface RegistrationAdministration {
  policy: RegistrationPolicy;
  allowlist: RegistrationAllowlistEntry[];
  invitations: RegistrationInvitation[];
  invitationUses: RegistrationInvitationUse[];
}

export interface AccountDevice {
  bindingId: string;
  deviceId: string;
  namespaceId: string;
  namespaceDisplayName: string;
  organizationId: string;
  organizationDisplayName: string;
  algorithm: string;
  hwidHint: string;
  trustLevel: string;
  virtualEnvironment: boolean;
  virtualConfidence: number | null;
  bindingSource: string;
  boundAt: string;
  lastSeenAt: string;
  deviceFirstSeenAt: string;
  deviceLastSeenAt: string;
}

export interface OAuthConnection {
  id: string;
  site: {
    clientId: string;
    protocolClientId: string;
    clientDisplayName: string;
    applicationType: string;
    applicationId: string;
    applicationDisplayName: string;
    organizationId: string;
    organizationDisplayName: string;
    environmentId: string;
    environmentDisplayName: string;
    environmentKind: string;
  };
  scopes: string[];
  claims: string[];
  connectedAt: string;
  lastUsedAt: string | null;
  activeAuthorizationCount: number;
  accessTokenExpiresAt: string | null;
  refreshTokenExpiresAt: string | null;
}

export interface OrganizationSummary {
  id: string;
  slug: string;
  displayName: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export interface OrganizationDetail extends OrganizationSummary {
  currentRoles: Array<{ id: string; key: string; kind: string; displayName: string }>;
  permissions: string[];
}

export interface OrganizationMember {
  id: string;
  subjectId: string;
  username: string;
  displayName: string;
  roles: Array<{ id: string; key: string; kind: string; displayName: string }>;
  joinedAt: string;
  version: number;
}

export interface DeveloperApplication {
  id: string;
  key: string;
  displayName: string;
  description: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export interface DeveloperEnvironment {
  id: string;
  key: string;
  displayName: string;
  kind: "DEVELOPMENT" | "STAGING" | "PRODUCTION";
  status: string;
  productionReviewState: string;
  createdAt: string;
  updatedAt: string;
  approvedAt: string | null;
  version: number;
}

export interface DeveloperApplicationDetail {
  application: DeveloperApplication;
  environments: DeveloperEnvironment[];
}

export interface OAuthClientSummary {
  id: string;
  key: string;
  clientId: string;
  displayName: string;
  description: string | null;
  clientType: "PUBLIC" | "CONFIDENTIAL";
  applicationType: "WEB" | "NATIVE" | "SERVICE";
  authenticationMethod: string;
  requirePkce: boolean;
  requireConsent: boolean;
  status: string;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export interface OAuthClientDetail extends OAuthClientSummary {
  redirectUris: string[];
  postLogoutRedirectUris: string[];
  scopes: string[];
}

export interface OAuthCredential {
  id: string;
  hint: string;
  status: string;
  createdAt: string;
  expiresAt: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
  updatedAt: string;
  version: number;
}

export interface OAuthCredentialSecret {
  id: string;
  secret: string;
  hint: string;
  createdAt: string;
  expiresAt: string | null;
}

export interface OAuthClientProvisioning {
  client: OAuthClientDetail;
  initialCredential: OAuthCredentialSecret | null;
}

export interface OAuthScope {
  id: string;
  name: string;
  displayName: string;
  description: string | null;
  kind: "STANDARD" | "CUSTOM";
  status: string;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export interface DeviceNamespace {
  id: string;
  key: string;
  displayName: string;
  description: string | null;
  kind: "PRIVATE" | "SHARED";
  status: string;
  activeEnvironmentCount: number;
  activeDeviceCount: number;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export interface DeviceNamespaceDetail {
  namespace: DeviceNamespace;
  environments: Array<{
    id: string;
    environmentId: string;
    boundAt: string;
    updatedAt: string;
    version: number;
  }>;
}

export interface DevicePolicy {
  id: string;
  enforcementMode: "OPTIONAL" | "REQUIRED";
  minimumTrustLevel: "SELF_ASSERTED" | "SDK_GENERATED" | "ATTESTED";
  allowVirtualEnvironments: boolean;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export interface ManagedDevice {
  id: string;
  hwidHint: string;
  algorithm: string;
  trustLevel: string;
  virtualEnvironment: boolean;
  virtualConfidence: number | null;
  status: string;
  activeSubjectCount: number;
  activeKeyCount: number;
  activeBanCount: number;
  firstSeenAt: string;
  lastSeenAt: string;
  updatedAt: string;
  version: number;
}

export interface ManagedDeviceKey {
  id: string;
  algorithm: string;
  thumbprint: string;
  status: "ACTIVE" | "REVOKED";
  firstSeenAt: string;
  lastSeenAt: string;
  revokedAt: string | null;
  revocationReason: string | null;
  updatedAt: string;
  version: number;
}

export interface DeviceBan {
  id: string;
  environmentId: string | null;
  targetType: "DEVICE" | "SUBJECT" | "KEY";
  targetDeviceId: string | null;
  targetSubjectId: string | null;
  targetKeyId: string | null;
  reasonCode: string;
  note: string | null;
  status: "ACTIVE" | "REVOKED";
  createdAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
  updatedAt: string;
  version: number;
}

export interface DeviceEvent {
  id: string;
  environmentId: string | null;
  clientId: string | null;
  deviceId: string | null;
  subjectId: string | null;
  type: string;
  outcome: string;
  reasonCode: string | null;
  metadata: Record<string, unknown>;
  occurredAt: string;
}

export interface DeveloperAuditEvent {
  id: string;
  actorType: string;
  action: string;
  targetType: string;
  outcome: string;
  requestId: string | null;
  occurredAt: string;
}

export interface ProductionReview {
  id: string;
  status: "SUBMITTED" | "APPROVED" | "REJECTED" | "CANCELLED";
  homepageUri: string;
  privacyPolicyUri: string;
  termsUri: string;
  securityContactHint: string;
  dataHandlingSummary: string;
  justification: string;
  submittedAt: string;
  reviewedAt: string | null;
  decisionReason: string | null;
  version: number;
}

export type OperatorPermission =
  | "PRODUCTION_REVIEW"
  | "PROFILE_SCHEMA"
  | "PROFILE_DATA"
  | "ACCOUNT_SUPPORT"
  | "ACCOUNT_ENFORCEMENT"
  | "RISK_REVIEW"
  | "AUDIT_READ"
  | "KEY_ROTATION"
  | "OPERATOR_ADMIN"
  | "SUPER_ADMIN";

export interface OperatorSubject {
  id: string;
  username: string | null;
  displayName: string | null;
  status: string;
  statusReason: string | null;
  suspendedUntil: string | null;
  createdAt: string;
}

export interface OperatorEnforcement {
  id: string;
  subjectId: string;
  username: string | null;
  type: string;
  status: string;
  reasonCode: string;
  reasonNote: string;
  effectiveAt: string;
  expiresAt: string | null;
  revocationReason: string | null;
  revokedAt: string | null;
  updatedAt: string;
  version: number;
}

export interface OperatorAppeal {
  id: string;
  subjectId: string;
  username: string | null;
  displayName: string | null;
  subjectStatus: string;
  category: string;
  status: string;
  priority: string;
  assignedTo: string | null;
  resolution: string | null;
  requestedAt: string;
  reviewedAt: string | null;
  resolvedAt: string | null;
  updatedAt: string;
  version: number;
}

export interface OperatorRiskCase {
  id: string;
  targetType: string;
  targetId: string | null;
  username: string | null;
  status: string;
  severity: string;
  category: string;
  summary: string;
  assignedTo: string | null;
  resolution: string | null;
  createdAt: string;
  investigatedAt: string | null;
  resolvedAt: string | null;
  updatedAt: string;
  version: number;
}

export interface OperatorAuditEvent {
  id: string;
  actorType: string;
  action: string;
  targetType: string;
  organizationId: string | null;
  outcome: string;
  requestId: string | null;
  occurredAt: string;
}

export interface OperatorKeyRing {
  ringType: string;
  activeKeyId: string;
  versions: Array<{ keyId: string; active: boolean; present: boolean; references: number }>;
}

export interface OperatorKeyRotation {
  id: string;
  ringType: string;
  targetKeyId: string;
  status: string;
  reason: string;
  requestedBy: string | null;
  approvedBy: string | null;
  requestedAt: string;
  approvedAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  updatedAt: string;
  version: number;
}

export interface OperatorAssignment {
  id: string;
  subjectId: string;
  username: string | null;
  displayName: string | null;
  permission: OperatorPermission;
  status: string;
  reason: string | null;
  grantedAt: string;
  revokedAt: string | null;
  version: number;
}

export interface SuperAdministratorSubject {
  id: string;
  username: string | null;
  displayName: string | null;
  email: string | null;
  emailVerified: boolean;
  emailVerifiedAt: string | null;
  locale: string;
  timeZone: string;
  status: string;
  statusReason: string | null;
  suspendedUntil: string | null;
  totpEnabled: boolean;
  passkeyCount: number;
  recoveryCodeCount: number;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export interface ImpersonationState {
  active: boolean;
  administratorId: string | null;
  administratorUsername: string | null;
  subjectId: string | null;
  subjectUsername: string | null;
  expiresAt: string | null;
}

export interface ProfileNamespace {
  publicId: string;
  ownerType: string;
  organizationId: string | null;
  key: string;
  displayName: string;
  description: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export interface ProfileField {
  publicId: string;
  key: string;
  displayName: string;
  description: string | null;
  valueType: string;
  sensitivity: "STANDARD" | "SENSITIVE";
  subjectWritable: boolean;
  ownerReadable: boolean;
  ownerWritable: boolean;
  claimReleasable: boolean;
  equalitySearchable: boolean;
  validation: Record<string, unknown>;
  status: string;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export interface ProfileNamespaceDetail {
  namespace: ProfileNamespace;
  fields: ProfileField[];
}

export interface ProfileDocument {
  namespaceId: string;
  namespaceKey: string;
  subjectId: string;
  documentId: string;
  version: number;
  values: Record<string, unknown>;
  sensitiveValues: Record<string, { present: boolean; value: unknown }>;
  ownerView: boolean;
  updatedAt: string;
}

export interface PrivacyStatus {
  accountStatus: string;
  deletionScheduledAt: string | null;
  terms: { version: string; acceptedAt: string };
  privacy: { version: string; acceptedAt: string };
  requests: Array<{
    id: string;
    type: "EXPORT" | "DELETE";
    status: string;
    requestedAt: string;
    eligibleAt: string | null;
    completedAt: string | null;
    artifactStatus: string | null;
    artifactExpiresAt: string | null;
  }>;
}

const mutationMethods = new Set(["POST", "PUT", "PATCH", "DELETE"]);

async function csrfHeaders(): Promise<Record<string, string>> {
  const response = await fetch("/api/v1/session/csrf", {
    credentials: "include",
    cache: "no-store",
  });
  if (!response.ok) {
    const error = await problem(response);
    if (error.code === "step-up-required" && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("evileye:step-up-required"));
    }
    throw error;
  }
  const token = (await response.json()) as { headerName: string; token: string };
  return { [token.headerName]: token.token };
}

async function problem(response: Response): Promise<ApiError> {
  let payload: Partial<ApiProblem> = {};
  try {
    payload = (await response.json()) as Partial<ApiProblem>;
  } catch {
    payload = {};
  }
  return new ApiError({
    status: response.status,
    code: payload.code ?? "request-failed",
    title: payload.title ?? "请求失败",
    detail: payload.detail ?? "请求暂时无法完成。",
    requestId: payload.requestId,
    violations: payload.violations,
  });
}

export async function api<T>(
  path: string,
  init: RequestInit & { csrf?: boolean } = {},
): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);
  if (init.body != null && !(init.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (mutationMethods.has(method) && init.csrf !== false) {
    const csrf = await csrfHeaders();
    Object.entries(csrf).forEach(([name, value]) => headers.set(name, value));
  }
  const { csrf: _csrf, ...requestInit } = init;
  const response = await fetch(path, {
    ...requestInit,
    method,
    headers,
    credentials: "include",
    cache: "no-store",
  });
  if (!response.ok) {
    const error = await problem(response);
    if (error.code === "step-up-required" && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("evileye:step-up-required"));
    }
    throw error;
  }
  if (response.status === 204 || response.headers.get("Content-Length") === "0") {
    return undefined as T;
  }
  return (await response.json()) as T;
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.violations.length > 0) {
      return error.violations.map((item) => item.message).join(" ");
    }
    return error.message;
  }
  if (error instanceof DOMException && error.name === "NotAllowedError") {
    return "验证已取消或超时。";
  }
  return error instanceof Error ? error.message : "请求暂时无法完成。";
}

function decodeBase64Url(value: string): Uint8Array<ArrayBuffer> {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(value.length / 4) * 4, "=");
  return Uint8Array.from(atob(base64), (character) => character.charCodeAt(0));
}

function encodeBase64Url(value: ArrayBuffer): string {
  let binary = "";
  for (const byte of new Uint8Array(value)) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function creationOptions(value: PublicKeyCredentialCreationOptionsJSON): PublicKeyCredentialCreationOptions {
  return {
    ...value,
    challenge: decodeBase64Url(value.challenge),
    user: { ...value.user, id: decodeBase64Url(value.user.id) },
    excludeCredentials: value.excludeCredentials?.map((item) => ({
      ...item,
      id: decodeBase64Url(item.id),
    })),
  };
}

function requestOptions(value: PublicKeyCredentialRequestOptionsJSON): PublicKeyCredentialRequestOptions {
  return {
    ...value,
    challenge: decodeBase64Url(value.challenge),
    allowCredentials: value.allowCredentials?.map((item) => ({
      ...item,
      id: decodeBase64Url(item.id),
    })),
  };
}

type PublicKeyCredentialCreationOptionsJSON = Omit<PublicKeyCredentialCreationOptions, "challenge" | "user" | "excludeCredentials"> & {
  challenge: string;
  user: Omit<PublicKeyCredentialUserEntity, "id"> & { id: string };
  excludeCredentials?: Array<Omit<PublicKeyCredentialDescriptor, "id"> & { id: string }>;
};

type PublicKeyCredentialRequestOptionsJSON = Omit<PublicKeyCredentialRequestOptions, "challenge" | "allowCredentials"> & {
  challenge: string;
  allowCredentials?: Array<Omit<PublicKeyCredentialDescriptor, "id"> & { id: string }>;
};

export async function authenticatePasskey(): Promise<void> {
  if (!window.PublicKeyCredential) throw new Error("当前浏览器不支持 Passkey。");
  const options = await api<PublicKeyCredentialRequestOptionsJSON>(
    "/api/v1/passkeys/authentication/options",
    { method: "POST" },
  );
  const credential = (await navigator.credentials.get({
    publicKey: requestOptions(options),
  })) as PublicKeyCredential | null;
  if (!credential) throw new Error("没有收到 Passkey 凭据。");
  const response = credential.response as AuthenticatorAssertionResponse;
  await api("/api/v1/passkeys/authentication", {
    method: "POST",
    body: JSON.stringify({
      id: credential.id,
      rawId: encodeBase64Url(credential.rawId),
      type: credential.type,
      response: {
        clientDataJSON: encodeBase64Url(response.clientDataJSON),
        authenticatorData: encodeBase64Url(response.authenticatorData),
        signature: encodeBase64Url(response.signature),
        userHandle: response.userHandle ? encodeBase64Url(response.userHandle) : null,
      },
      clientExtensionResults: credential.getClientExtensionResults(),
    }),
  });
}

export async function registerPasskey(label: string): Promise<PasskeyView> {
  if (!window.PublicKeyCredential) throw new Error("当前浏览器不支持 Passkey。");
  const options = await api<PublicKeyCredentialCreationOptionsJSON>(
    "/api/v1/passkeys/registration/options",
    { method: "POST" },
  );
  const credential = (await navigator.credentials.create({
    publicKey: creationOptions(options),
  })) as PublicKeyCredential | null;
  if (!credential) throw new Error("没有收到 Passkey 凭据。");
  const response = credential.response as AuthenticatorAttestationResponse;
  return api<PasskeyView>("/api/v1/passkeys", {
    method: "POST",
    body: JSON.stringify({
      label,
      credential: {
        id: credential.id,
        rawId: encodeBase64Url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: encodeBase64Url(response.clientDataJSON),
          attestationObject: encodeBase64Url(response.attestationObject),
          transports: response.getTransports?.() ?? [],
        },
        clientExtensionResults: credential.getClientExtensionResults(),
      },
    }),
  });
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return "从未";
  return new Intl.DateTimeFormat("zh-TW", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}
